import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  listCompanyDatabases,
  duplicateDatabaseForCompany,
  addDatabaseToCompany,
  setActiveDatabase,
  removeDatabaseFromCompany,
  moveDatabaseToCompany,
  updateDatabaseLabel,
  CompanyDatabaseModel,
  DuplicateDatabaseForCompanyRequest,
  AddDatabaseToCompanyRequest,
  SetActiveDatabaseRequest,
  RemoveDatabaseFromCompanyRequest,
  MoveDatabaseToCompanyRequest,
  UpdateDatabaseLabelRequest,
} from './companyDatabases'
import useCompanyService from '@/modules/drivers/services/CompanyService'

const TERMINAL_STATUSES = new Set(['SUCCESS', 'ERROR'])

function hasPendingDatabases(data: CompanyDatabaseModel[] | undefined): boolean {
  return !!data?.some((db) => !TERMINAL_STATUSES.has(db.status ?? ''))
}

export function isPendingDatabaseStatus(status: string | null | undefined): boolean {
  return !TERMINAL_STATUSES.has(status ?? '')
}

function hasMigratedDatabase(data: CompanyDatabaseModel[] | undefined): boolean {
  return !!data?.some((db) => db.isCompatibleWithModel === true)
}

// The API opens exactly one database per company — the active one (CompanyDatabases.IsActive
// mirrors the company's DatabaseServer FK). Sibling databases say nothing about whether the
// company's requests will work, so readiness must be judged on this one alone.
export function findActiveDatabase(
  data: CompanyDatabaseModel[] | undefined,
): CompanyDatabaseModel | null {
  if (!data || data.length === 0) return null
  return data.find((db) => db.isActive) ?? (data.length === 1 ? data[0] : null)
}

// Mirrors the server gate in EF6SuperTachoContextFactory (!IsCompatibleWithModel ||
// Status != SUCCESS): below this bar every request for the company throws
// NOT_MIGRATED_COMPANY.
export function isUsableDatabase(db: CompanyDatabaseModel | null | undefined): boolean {
  return !!db && db.isCompatibleWithModel === true && db.status === 'SUCCESS'
}

// SUCCESS on an old schema means the restore finished but the migration job hasn't caught
// up — still preparing, even though the status itself is terminal.
export function isPreparingDatabase(db: CompanyDatabaseModel | null | undefined): boolean {
  if (!db) return false
  if (isMigrationFailedDatabase(db)) return false
  return (
    isPendingDatabaseStatus(db.status) ||
    (db.status === 'SUCCESS' && db.isCompatibleWithModel !== true)
  )
}

// The migration job gave up after repeated failures — waiting won't help.
export function isMigrationFailedDatabase(
  db: CompanyDatabaseModel | null | undefined,
): boolean {
  return !!db && db.migrationFailed === true && db.isCompatibleWithModel !== true
}

interface UseQueryCompanyDatabasesOptions {
  // Keep polling until a database is migrated (compatible with the current model),
  // not just until its restore/creation status is terminal. A restored .bak reaches
  // status=SUCCESS while still on an old schema (isCompatibleWithModel=false) until the
  // migration job catches it up — opening it before then throws NOT_MIGRATED_COMPANY.
  waitForMigration?: boolean
  // Poll until this predicate holds. Takes precedence over waitForMigration, for callers
  // whose definition of "done waiting" isn't "any database is migrated".
  pollUntil?: (data: CompanyDatabaseModel[] | undefined) => boolean
}

export function useQueryCompanyDatabases(
  companyId: string | null,
  options: UseQueryCompanyDatabasesOptions = {},
) {
  return useQuery({
    queryKey: ['company-databases', companyId],
    queryFn: async () => {
      const { data } = await listCompanyDatabases(companyId!)
      return data
    },
    enabled: companyId != null,
    refetchInterval: (query) => {
      const data = query.state.data
      if (options.pollUntil) {
        return options.pollUntil(data) ? false : 3000
      }
      if (options.waitForMigration) {
        return hasMigratedDatabase(data) ? false : 3000
      }
      return hasPendingDatabases(data) ? 3000 : false
    },
  })
}

export function useMutationDuplicateDatabase(companyId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (request: DuplicateDatabaseForCompanyRequest) =>
      duplicateDatabaseForCompany(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-databases', companyId] })
      queryClient.invalidateQueries({ queryKey: ['config.companies'] })
    },
  })
}

export function useMutationAddDatabase(companyId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (request: AddDatabaseToCompanyRequest) =>
      addDatabaseToCompany(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-databases', companyId] })
      queryClient.invalidateQueries({ queryKey: ['config.companies'] })
    },
  })
}

export function useMutationSetActiveDatabase(companyId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (request: SetActiveDatabaseRequest) =>
      setActiveDatabase(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-databases', companyId] })
      queryClient.invalidateQueries({ queryKey: ['config.companies'] })
    },
  })
}

export function useMutationRemoveDatabase(companyId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (request: RemoveDatabaseFromCompanyRequest) =>
      removeDatabaseFromCompany(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-databases', companyId] })
      queryClient.invalidateQueries({ queryKey: ['config.companies'] })
    },
  })
}

export function useMutationMoveDatabase(sourceCompanyId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (request: MoveDatabaseToCompanyRequest) =>
      moveDatabaseToCompany(request),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['company-databases', sourceCompanyId] })
      queryClient.invalidateQueries({ queryKey: [
        'company-databases',
        variables.destinationCompanyId,
      ] })
      queryClient.invalidateQueries({ queryKey: ['config.companies'] })
    },
  })
}

export function useQueryCompanyDatabaseSizes(companyId: string | null) {
  const { getCompanyDatabaseSizes } = useCompanyService()
  return useQuery({
    queryKey: ['company-database-sizes', companyId],
    queryFn: () => getCompanyDatabaseSizes(companyId!),
    enabled: companyId != null,
  })
}

export function useQueryCompanyDatabaseSizesBulk(companyIds: string[]) {
  const { getCompanyDatabaseSizesBulk } = useCompanyService()
  // Sort to keep the query key stable regardless of row ordering.
  const sortedIds = [...companyIds].sort()
  return useQuery({
    queryKey: ['company-database-sizes-bulk', sortedIds],
    queryFn: () => getCompanyDatabaseSizesBulk(sortedIds),
    enabled: sortedIds.length > 0,
  })
}

export function useMutationUpdateDatabaseLabel(companyId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (request: UpdateDatabaseLabelRequest) =>
      updateDatabaseLabel(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-databases', companyId] })
      queryClient.invalidateQueries({ queryKey: ['config.companies'] })
    },
  })
}
